import React, { useCallback, useEffect, useRef } from "react";
import useControllableState from "components/common/hooks/useControllableState";
import useMergeRef from "components/common/hooks/useMergeRef";
import FloatingPopoverPanel from "./FloatingPopoverPanel";
import {
  DEFAULT_MAX_WIDTH,
  DEFAULT_Z_INDEX,
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
  FLOATING_POPOVER_VARIANT,
} from "./constants";

const composeHandlers = (theirs, ours) => (event) => {
  theirs?.(event);
  if (event?.defaultPrevented) return;
  ours?.(event);
};

/**
 * Generic popover built on react-bootstrap Overlay + Popover.
 *
 * Modes:
 * - hover: open on hover, stay open while pointer is on trigger or panel, close on leave
 * - click: toggle on trigger click, close on outside click or trigger click again
 * - tooltip: open on hover, close on leave (panel is not interactive)
 *
 * Use `anchorRef` when the arrow should point at a nested element (e.g. icon inside a button).
 */
const FloatingPopover = ({
  mode = FLOATING_POPOVER_MODE.HOVER,
  children,
  content,
  anchorRef,
  placement = FLOATING_POPOVER_PLACEMENT.TOP,
  variant = FLOATING_POPOVER_VARIANT.DEFAULT,
  openDelay,
  closeDelay,
  maxWidth = DEFAULT_MAX_WIDTH,
  showArrow = true,
  disabled = false,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  zIndex = DEFAULT_Z_INDEX,
  className = "",
  contentClassName = "",
  offset,
  preventFlip = false,
}) => {
  const triggerRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const isTooltip = mode === FLOATING_POPOVER_MODE.TOOLTIP;
  const isClick = mode === FLOATING_POPOVER_MODE.CLICK;
  const isHover = mode === FLOATING_POPOVER_MODE.HOVER || isTooltip;
  const interactive = !isTooltip;

  const resolvedOpenDelay = openDelay ?? (isTooltip ? 300 : 200);
  const resolvedCloseDelay = closeDelay ?? (isTooltip ? 0 : 120);

  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const referenceRef = anchorRef || triggerRef;

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const setOpenSafe = useCallback(
    (nextOpen) => {
      if (disabled) return;
      setOpen(nextOpen);
    },
    [disabled, setOpen]
  );

  const openPopover = useCallback(() => {
    clearCloseTimer();
    clearOpenTimer();
    if (isClick) {
      setOpenSafe(true);
      return;
    }
    openTimerRef.current = setTimeout(() => {
      setOpenSafe(true);
    }, resolvedOpenDelay);
  }, [
    clearCloseTimer,
    clearOpenTimer,
    isClick,
    resolvedOpenDelay,
    setOpenSafe,
  ]);

  const closePopover = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    if (isClick) {
      setOpenSafe(false);
      return;
    }
    if (resolvedCloseDelay <= 0) {
      setOpenSafe(false);
      return;
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenSafe(false);
    }, resolvedCloseDelay);
  }, [
    clearCloseTimer,
    clearOpenTimer,
    isClick,
    resolvedCloseDelay,
    setOpenSafe,
  ]);

  const handleTriggerMouseEnter = useCallback(() => {
    if (!isHover || disabled) return;
    openPopover();
  }, [disabled, isHover, openPopover]);

  const handleTriggerMouseLeave = useCallback(() => {
    if (!isHover || disabled) return;
    closePopover();
  }, [closePopover, disabled, isHover]);

  const handlePanelMouseEnter = useCallback(() => {
    if (!interactive || disabled) return;
    clearCloseTimer();
    clearOpenTimer();
    setOpenSafe(true);
  }, [clearCloseTimer, clearOpenTimer, disabled, interactive, setOpenSafe]);

  const handlePanelMouseLeave = useCallback(() => {
    if (!interactive || disabled) return;
    closePopover();
  }, [closePopover, disabled, interactive]);

  const handleTriggerClick = useCallback(
    (event) => {
      if (!isClick || disabled) return;
      setOpen((prev) => !prev);
    },
    [disabled, isClick, setOpen]
  );

  const handleRootClose = useCallback(() => {
    if (isClick) {
      setOpenSafe(false);
    }
  }, [isClick, setOpenSafe]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearCloseTimer, clearOpenTimer]
  );

  const child = React.Children.only(children);
  const mergedRef = useMergeRef(child.ref, triggerRef);

  const trigger = React.cloneElement(child, {
    ref: mergedRef,
    onMouseEnter: composeHandlers(child.props.onMouseEnter, handleTriggerMouseEnter),
    onMouseLeave: composeHandlers(child.props.onMouseLeave, handleTriggerMouseLeave),
    onClick: composeHandlers(child.props.onClick, handleTriggerClick),
    "aria-expanded": isClick ? Boolean(open) : undefined,
  });

  return (
    <>
      {trigger}
      <FloatingPopoverPanel
        open={Boolean(open) && !disabled}
        referenceRef={referenceRef}
        variant={variant}
        placement={placement}
        maxWidth={maxWidth}
        zIndex={zIndex}
        showArrow={showArrow}
        offsetPx={offset}
        interactive={interactive}
        preventFlip={preventFlip}
        rootClose={isClick}
        onHide={handleRootClose}
        className={className}
        contentClassName={contentClassName}
        onMouseEnter={handlePanelMouseEnter}
        onMouseLeave={handlePanelMouseLeave}
      >
        {typeof content === "function"
          ? content({ close: () => setOpenSafe(false) })
          : content}
      </FloatingPopoverPanel>
    </>
  );
};

export default FloatingPopover;
